import React, { useState, useEffect } from 'react';
import { useSignUp, useAuth } from '@clerk/clerk-react';
import { useHistory, Link, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';

const SignUpPage = () => {
  const { signUp, setActive, isLoaded: signUpLoaded } = useSignUp();
  const { isSignedIn, isLoaded } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if user comes from invitation (has __clerk_ticket in URL)
  const searchParams = new URLSearchParams(location.search);
  const hasInvitation = searchParams.has('__clerk_ticket');

  // Redirect if already signed in
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      history.push('/');
    }
  }, [isLoaded, isSignedIn, history]);

  // Pre-fill email from invitation when SignUp is loaded
  useEffect(() => {
    if (hasInvitation && signUpLoaded && signUp) {
      // If email is already in the signUp object, pre-fill it
      if (signUp.emailAddress) {
        setEmail(signUp.emailAddress);
      }
    }
  }, [hasInvitation, signUpLoaded, signUp, searchParams]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate passwords match (only for non-invitation signups)
    if (!hasInvitation && password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Check if signUp is loaded
    if (!signUpLoaded || !signUp) {
      setError('Sign up not ready. Please try again.');
      setLoading(false);
      return;
    }

    // Helper function to determine redirect path based on email domain
    const getRedirectPath = (userEmail) => {
      const isAdminDomain =
        userEmail.endsWith('@samxlabs.com') ||
        userEmail.endsWith('@adlerlocal.com') ||
        userEmail.endsWith('@adlersocial.com');
      return isAdminDomain ? '/clients' : '/client';
    };

    try {
      if (hasInvitation) {
        // For invitations, only update with password (email is already set from ticket)
        const updateResult = await signUp.update({
          password: password
        });

        // Check if sign up is complete after update
        if (updateResult.status === 'complete') {
          await setActive({ session: updateResult.createdSessionId });
          const redirectPath = getRedirectPath(
            updateResult.emailAddress || email
          );
          console.log(
            'Signup complete via invitation, redirecting to:',
            redirectPath
          );
          history.push(redirectPath);
        } else if (
          updateResult.unverifiedFields &&
          updateResult.unverifiedFields.includes('email_address')
        ) {
          // Email needs verification with ticket strategy
          const verifyResult =
            await updateResult.attemptEmailAddressVerification({
              strategy: 'ticket'
            });

          if (verifyResult.status === 'complete') {
            await setActive({ session: verifyResult.createdSessionId });
            const redirectPath = getRedirectPath(
              verifyResult.emailAddress || email
            );
            console.log(
              'Signup complete after verification, redirecting to:',
              redirectPath
            );
            history.push(redirectPath);
          } else {
            console.error(
              'Invitation verification failed:',
              verifyResult.status
            );
            setError('Unable to verify email. Please try again.');
          }
        } else if (updateResult.status === 'missing_requirements') {
          setError(
            `Missing required fields: ${updateResult.missingFields.join(', ')}`
          );
        } else {
          console.error('Unexpected signup status:', updateResult.status);
          setError('Unable to complete registration. Please try again.');
        }
      } else {
        // Regular sign up flow
        const result = await signUp.create({
          emailAddress: email,
          password: password,
          firstName: firstName,
          lastName: lastName
        });

        // Send email verification code
        await signUp.prepareEmailAddressVerification({
          strategy: 'email_code'
        });

        if (result.status === 'complete') {
          await setActive({ session: result.createdSessionId });
          const redirectPath = getRedirectPath(email);
          console.log('Regular signup complete, redirecting to:', redirectPath);
          history.push(redirectPath);
        } else if (result.status === 'missing_requirements') {
          setError('Please check your email for a verification code');
        }
      }
    } catch (err) {
      console.error('Error during sign up:', err);
      setError(
        err.errors?.[0]?.message || err.message || 'Failed to create account'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      {/* Logo */}
      <div className="mb-8">
        <img
          src="../assets/img/adlerlocal_logo_2026.png"
          alt="Adler Local"
          className="h-10"
        />
      </div>

      {/* Sign Up Card */}
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="mb-6 text-center">
            <h1 className="text-xl font-semibold tracking-tight">
              {hasInvitation ? 'Complete your account' : 'Create your account'}
            </h1>
            {hasInvitation && (
              <p className="text-sm text-muted-foreground mt-2">
                Set your password to complete the registration
              </p>
            )}
          </div>

          <form onSubmit={handleSignUp} className="space-y-4">
            {!hasInvitation && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            <div>
              <Input
                id="email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading || hasInvitation}
                className={hasInvitation ? 'bg-muted' : ''}
              />
            </div>

            <div>
              <Input
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            {!hasInvitation && (
              <div>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            )}

            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full !bg-[#3766a8] hover:!bg-[#2d5286] !text-white !border-0"
              disabled={loading}>
              {loading
                ? 'Creating account...'
                : hasInvitation
                ? 'Complete Registration'
                : 'Sign Up'}
            </Button>
          </form>

          {/* Sign In Link - only show if not from invitation */}
          {!hasInvitation && (
            <div className="mt-4 text-center text-sm">
              <span className="text-muted-foreground">
                Already have an account?{' '}
              </span>
              <Link to="/sign-in" className="text-[#3766a8] hover:underline">
                Sign in
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SignUpPage;
